<template>
    <div class="dtp" v-if="!headless && !global">
        <div
            class="dtp-input-wrap"
            :class="[disabled ? 'dtp-input-wrap--disabled' : '', errorClass || '']"
            @click="toggle"
            ref="trigger"
        >
            <svg class="dtp-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span class="dtp-input-text" :class="{'dtp-input-placeholder': !displayValue}">
                {{ displayValue || placeholder || defaultPlaceholder }}
            </span>
            <button
                v-if="clearable && modelValue"
                class="dtp-input-clear"
                @click.stop="clearValue"
                type="button"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </button>
            <svg class="dtp-input-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
            </svg>
        </div>
    </div>

    <teleport to="body" v-if="teleported">
        <transition name="dtp-fade">
            <div
                v-if="isOpen"
                class="dtp-overlay"
                :class="{'dtp-overlay--modal': isModal}"
                @click.self="onOverlayClick"
            >
                <div
                    class="dtp-panel"
                    :class="{'dtp-panel--modal': isModal, 'dtp-panel--above': dropAbove}"
                    :style="panelStyle"
                    ref="panel"
                >
                    <div class="dtp-header">
                        <button class="dtp-nav-btn" @click="navPrev" type="button">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="15 18 9 12 15 6"/>
                            </svg>
                        </button>
                        <button class="dtp-header-label" @click="cycleView" type="button">
                            <template v-if="viewMode === 'calendar'">{{ monthName }} {{ viewYear }}</template>
                            <template v-else-if="viewMode === 'months'">{{ viewYear }}</template>
                            <template v-else>{{ yearRangeStart }} - {{ yearRangeStart + 11 }}</template>
                        </button>
                        <button class="dtp-nav-btn" @click="navNext" type="button">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </button>
                    </div>

                    <div v-if="viewMode === 'calendar'" class="dtp-calendar">
                        <div class="dtp-weekdays">
                            <span v-for="d in weekdayLabels" :key="d">{{ d }}</span>
                        </div>
                        <div class="dtp-days">
                            <button
                                v-for="(day, i) in calendarDays"
                                :key="i"
                                class="dtp-day"
                                :class="{
                                    'dtp-day--other': day.other,
                                    'dtp-day--today': day.today,
                                    'dtp-day--selected': day.selected,
                                    'dtp-day--disabled': day.disabled,
                                }"
                                :disabled="day.disabled"
                                @click="selectDay(day)"
                                type="button"
                            >{{ day.num }}
                            </button>
                        </div>
                    </div>

                    <div v-else-if="viewMode === 'months'" class="dtp-grid">
                        <button
                            v-for="(m, i) in monthLabels"
                            :key="i"
                            class="dtp-grid-item"
                            :class="{
                                'dtp-grid-item--selected': viewYear === pendingYear && i === pendingMonth,
                                'dtp-grid-item--current': viewYear === todayYear && i === todayMonth,
                            }"
                            @click="selectMonth(i)"
                            type="button"
                        >{{ m }}
                        </button>
                    </div>

                    <div v-else class="dtp-grid">
                        <button
                            v-for="y in yearGrid"
                            :key="y"
                            class="dtp-grid-item"
                            :class="{
                                'dtp-grid-item--selected': y === pendingYear,
                                'dtp-grid-item--current': y === todayYear,
                            }"
                            @click="selectYear(y)"
                            type="button"
                        >{{ y }}
                        </button>
                    </div>

                    <div v-if="effectiveType === 'datetime' && viewMode === 'calendar'" class="dtp-time">
                        <div class="dtp-time-col" ref="hourCol">
                            <div class="dtp-time-spacer"></div>
                            <button
                                v-for="h in 24"
                                :key="'h'+(h-1)"
                                class="dtp-time-item"
                                :class="{
                                    'dtp-time-item--selected': (h-1) === pendingHour,
                                    'dtp-time-item--disabled': isHourDisabled(h-1),
                                }"
                                :disabled="isHourDisabled(h-1)"
                                @click="setHour(h-1)"
                                type="button"
                            >{{ String(h - 1).padStart(2, '0') }}
                            </button>
                            <div class="dtp-time-spacer"></div>
                        </div>
                        <div class="dtp-time-sep">:</div>
                        <div class="dtp-time-col" ref="minuteCol">
                            <div class="dtp-time-spacer"></div>
                            <button
                                v-for="m in 60"
                                :key="'m'+(m-1)"
                                class="dtp-time-item"
                                :class="{
                                    'dtp-time-item--selected': (m-1) === pendingMinute,
                                    'dtp-time-item--disabled': isMinuteDisabled(m-1),
                                }"
                                :disabled="isMinuteDisabled(m-1)"
                                @click="setMinute(m-1)"
                                type="button"
                            >{{ String(m - 1).padStart(2, '0') }}
                            </button>
                            <div class="dtp-time-spacer"></div>
                        </div>
                        <template v-if="effectiveSeconds">
                            <div class="dtp-time-sep">:</div>
                            <div class="dtp-time-col" ref="secondCol">
                                <div class="dtp-time-spacer"></div>
                                <button
                                    v-for="s in 60"
                                    :key="'s'+(s-1)"
                                    class="dtp-time-item"
                                    :class="{
                                        'dtp-time-item--selected': (s-1) === pendingSecond,
                                        'dtp-time-item--disabled': isSecondDisabled(s-1),
                                    }"
                                    :disabled="isSecondDisabled(s-1)"
                                    @click="setSecond(s-1)"
                                    type="button"
                                >{{ String(s - 1).padStart(2, '0') }}
                                </button>
                                <div class="dtp-time-spacer"></div>
                            </div>
                        </template>
                    </div>

                    <div class="dtp-footer">
                        <button
                            v-if="!isTodayVisible"
                            class="dtp-footer-btn dtp-footer-btn--today"
                            @click="goToday"
                            type="button"
                        >
                            {{ t('today') }}
                        </button>
                        <span v-else></span>
                        <div class="dtp-footer-right">
                            <button class="dtp-footer-btn dtp-footer-btn--cancel" @click="cancelPicker" type="button">
                                {{ t('cancel') }}
                            </button>
                            <button
                                v-if="effectiveType === 'datetime'"
                                class="dtp-footer-btn dtp-footer-btn--confirm"
                                :disabled="!isTimeSelectionValid"
                                @click="confirmPicker"
                                type="button"
                            >
                                {{ t('confirm') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
const FALLBACK_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
const FALLBACK_MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']
const FALLBACK_WEEKDAYS = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz']

const pad = (n) => String(n).padStart(2, '0')

function parseValue(val) {
    if (!val) return null
    const parts = val.split(' ')
    const dateParts = parts[0].split('-')
    const y = parseInt(dateParts[0], 10)
    const m = parseInt(dateParts[1], 10) - 1
    const d = parseInt(dateParts[2], 10)
    let h = 0, min = 0, sec = 0
    if (parts[1]) {
        const timeParts = parts[1].split(':')
        h = parseInt(timeParts[0], 10)
        min = parseInt(timeParts[1], 10)
        if (timeParts[2] !== undefined) sec = parseInt(timeParts[2], 10)
    }
    return {year: y, month: m, day: d, hour: h, minute: min, second: sec}
}

function formatValue(year, month, day, hour, minute, second, type, withSeconds) {
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`
    if (type === 'date') return dateStr
    const timeStr = withSeconds ? `${pad(hour)}:${pad(minute)}:${pad(second)}` : `${pad(hour)}:${pad(minute)}`
    return `${dateStr} ${timeStr}`
}

function formatDisplay(val, type, withSeconds) {
    if (!val) return ''
    const p = parseValue(val)
    if (!p) return ''
    const dateStr = `${pad(p.day)}.${pad(p.month + 1)}.${p.year}`
    if (type === 'date') return dateStr
    const timeStr = withSeconds ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : `${pad(p.hour)}:${pad(p.minute)}`
    return `${dateStr} ${timeStr}`
}

function normalizeDate(d) {
    if (!d) return null
    if (d instanceof Date) return d
    if (typeof d === 'string') {
        const p = parseValue(d.replace('T', ' '))
        if (p) return new Date(p.year, p.month, p.day, p.hour, p.minute, p.second)
    }
    return null
}

function startOfDay(date) {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
}

export default {
    name: 'DateTimePicker',
    props: {
        modelValue: {type: String, default: null}, // v-model degeri: 'YYYY-MM-DD HH:mm' veya 'YYYY-MM-DD' veya 'YYYY-MM-DD HH:mm:ss'
        type: {type: String, default: 'datetime'}, // 'date': sadece tarih, 'datetime': tarih + saat
        placeholder: {type: String, default: ''}, // input placeholder metni
        disabled: {type: Boolean, default: false}, // picker'i devre disi birakir
        clearable: {type: Boolean, default: false}, // input icinde temizle (x) butonu gosterir
        min: {type: [Date, String], default: null}, // secilebilecek en erken tarih (Date veya string)
        max: {type: [Date, String], default: null}, // secilebilecek en gec tarih, saat dahil kisitlar
        disabledDate: {type: Function, default: null}, // (date: Date) => boolean, true ise gun disable
        modal: {type: Boolean, default: false}, // her zaman modal olarak acar
        autoModal: {type: Boolean, default: false}, // dropdown sigmayinca otomatik modal'a gecer
        teleported: {type: Boolean, default: true}, // panel'i body'ye teleport eder (z-index icin)
        errorClass: {type: String, default: ''}, // hata durumunda input'a eklenen css class (orn: 'dtp--error')
        seconds: {type: Boolean, default: false}, // true ise saat:dakika:saniye gosterir, varsayilan kapali
        global: {type: Boolean, default: false}, // input render etmez, sadece EventBus/$refs ile kullanilir (default.vue gibi)
    },
    emits: ['update:modelValue', 'change', 'open', 'close'],
    data() {
        const now = new Date()
        return {
            isOpen: false,
            isModal: false,
            dropAbove: false,
            panelStyle: {},
            viewMode: 'calendar',
            viewYear: now.getFullYear(),
            viewMonth: now.getMonth(),
            pendingDate: null,
            pendingHour: now.getHours(),
            pendingMinute: now.getMinutes(),
            pendingSecond: 0,
            headless: false,
            pendingResolve: null,
            effectiveType: this.type,
            effectiveSeconds: this.seconds,
            effectiveMin: null,
            effectiveMax: null,
            effectiveDisabledDate: null,
        }
    },
    computed: {
        displayValue() {
            return formatDisplay(this.modelValue, this.type, this.seconds)
        },
        defaultPlaceholder() {
            return this.type === 'date' ? this.t('selectDate') : this.t('selectDateTime')
        },
        pendingYear() {
            if (!this.pendingDate) return this.viewYear
            return parseInt(this.pendingDate.split('-')[0], 10)
        },
        pendingMonth() {
            if (!this.pendingDate) return this.viewMonth
            return parseInt(this.pendingDate.split('-')[1], 10) - 1
        },
        pendingDay() {
            if (!this.pendingDate) return null
            return parseInt(this.pendingDate.split('-')[2], 10)
        },
        todayYear() {
            return new Date().getFullYear()
        },
        todayMonth() {
            return new Date().getMonth()
        },
        todayDay() {
            return new Date().getDate()
        },
        isTodayVisible() {
            return this.viewMode === 'calendar' && this.viewYear === this.todayYear && this.viewMonth === this.todayMonth
        },
        todayDateStr() {
            const now = new Date()
            return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
        },
        monthName() {
            if (this.$t) {
                const key = `datepicker.months.${this.viewMonth}`
                const val = this.$t(key)
                if (val !== key) return val
            }
            return FALLBACK_MONTHS[this.viewMonth]
        },
        monthLabels() {
            return Array.from({length: 12}, (_, i) => {
                if (this.$t) {
                    const key = `datepicker.monthsShort.${i}`
                    const val = this.$t(key)
                    if (val !== key) return val
                }
                return FALLBACK_MONTHS_SHORT[i]
            })
        },
        weekdayLabels() {
            return Array.from({length: 7}, (_, i) => {
                if (this.$t) {
                    const key = `datepicker.weekdays.${i}`
                    const val = this.$t(key)
                    if (val !== key) return val
                }
                return FALLBACK_WEEKDAYS[i]
            })
        },
        minDateStr() {
            if (!this.resolvedMin) return null
            const d = this.resolvedMin
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        },
        maxDateStr() {
            if (!this.resolvedMax) return null
            const d = this.resolvedMax
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        },
        yearRangeStart() {
            return Math.floor(this.viewYear / 12) * 12
        },
        yearGrid() {
            const start = this.yearRangeStart
            return Array.from({length: 12}, (_, i) => start + i)
        },
        resolvedMin() {
            return normalizeDate(this.effectiveMin || this.min)
        },
        resolvedMax() {
            return normalizeDate(this.effectiveMax || this.max)
        },
        activeDisabledDate() {
            return this.effectiveDisabledDate || this.disabledDate
        },
        isTimeSelectionValid() {
            if (this.effectiveType !== 'datetime') return true
            if (!this.pendingDate) return false
            if (this.isHourDisabled(this.pendingHour)) return false
            if (this.isMinuteDisabled(this.pendingMinute)) return false
            if (this.effectiveSeconds && this.isSecondDisabled(this.pendingSecond)) return false
            return true
        },
        calendarDays() {
            const year = this.viewYear
            const month = this.viewMonth
            const firstDay = new Date(year, month, 1)
            let startWeekday = firstDay.getDay()
            startWeekday = startWeekday === 0 ? 6 : startWeekday - 1
            const daysInMonth = new Date(year, month + 1, 0).getDate()
            const prevMonthDays = new Date(year, month, 0).getDate()

            const days = []
            const today = new Date()
            const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`

            for (let i = startWeekday - 1; i >= 0; i--) {
                const d = prevMonthDays - i
                const m = month - 1 < 0 ? 11 : month - 1
                const y = month - 1 < 0 ? year - 1 : year
                const dateStr = `${y}-${pad(m + 1)}-${pad(d)}`
                days.push({
                    num: d, date: dateStr, other: true,
                    today: dateStr === todayStr,
                    selected: dateStr === this.pendingDate,
                    disabled: this.isDayDisabled(y, m, d),
                })
            }

            for (let d = 1; d <= daysInMonth; d++) {
                const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`
                days.push({
                    num: d, date: dateStr, other: false,
                    today: dateStr === todayStr,
                    selected: dateStr === this.pendingDate,
                    disabled: this.isDayDisabled(year, month, d),
                })
            }

            const remaining = 42 - days.length
            for (let d = 1; d <= remaining; d++) {
                const m = month + 1 > 11 ? 0 : month + 1
                const y = month + 1 > 11 ? year + 1 : year
                const dateStr = `${y}-${pad(m + 1)}-${pad(d)}`
                days.push({
                    num: d, date: dateStr, other: true,
                    today: dateStr === todayStr,
                    selected: dateStr === this.pendingDate,
                    disabled: this.isDayDisabled(y, m, d),
                })
            }

            return days
        },
    },
    watch: {
        modelValue: {
            handler(val) {
                if (!this.isOpen) this.syncFromValue(val)
            },
            immediate: true,
        },
        type(val) {
            if (!this.headless) this.effectiveType = val
        },
        seconds(val) {
            if (!this.headless) this.effectiveSeconds = val
        },
        resolvedMin() {
            if (this.isOpen) this.clampTime()
        },
    },
    methods: {
        t(key) {
            const keys = {
                today: 'common.today',
                cancel: 'common.cancel',
                confirm: 'btn.ok',
                selectDate: 'common.chooseDate',
                selectDateTime: 'common.chooseDateAndTime',
            }
            if (keys[key] && this.$t) return this.$t(keys[key])
            const fallback = {today: 'Bugün', cancel: 'İptal', confirm: 'Tamam', selectDate: 'Tarih seçin', selectDateTime: 'Tarih ve saat seçin'}
            return fallback[key] || key
        },
        syncFromValue(val) {
            const p = parseValue(val)
            if (p) {
                this.viewYear = p.year
                this.viewMonth = p.month
                this.pendingDate = `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`
                this.pendingHour = p.hour
                this.pendingMinute = p.minute
                this.pendingSecond = p.second
            } else {
                const ref = this.resolvedMin || new Date()
                this.viewYear = ref.getFullYear()
                this.viewMonth = ref.getMonth()
                this.pendingDate = null
                this.pendingHour = ref.getHours()
                this.pendingMinute = ref.getMinutes()
                this.pendingSecond = 0
            }
        },
        _hourHasAvailableMinute(h, dateStr) {
            let firstMin = 0, lastMin = 59
            if (this.resolvedMin && dateStr === this.minDateStr) {
                if (h < this.resolvedMin.getHours()) return false
                if (h === this.resolvedMin.getHours()) firstMin = this.resolvedMin.getMinutes()
            }
            if (this.resolvedMax && dateStr === this.maxDateStr) {
                if (h > this.resolvedMax.getHours()) return false
                if (h === this.resolvedMax.getHours()) lastMin = this.resolvedMax.getMinutes() - 1
            }
            return firstMin <= lastMin
        },
        isHourDisabled(h) {
            if (!this.pendingDate) return true
            if (this.resolvedMin && this.pendingDate === this.minDateStr) {
                if (h < this.resolvedMin.getHours()) return true
            }
            if (this.resolvedMax && this.pendingDate === this.maxDateStr) {
                if (h > this.resolvedMax.getHours()) return true
            }
            if (!this._hourHasAvailableMinute(h, this.pendingDate)) return true
            return false
        },
        isMinuteDisabled(m) {
            if (!this.pendingDate) return true
            if (this.resolvedMin && this.pendingDate === this.minDateStr) {
                if (this.pendingHour === this.resolvedMin.getHours() && m < this.resolvedMin.getMinutes()) return true
                if (this.pendingHour < this.resolvedMin.getHours()) return true
            }
            if (this.resolvedMax && this.pendingDate === this.maxDateStr) {
                if (this.pendingHour === this.resolvedMax.getHours() && m >= this.resolvedMax.getMinutes()) return true
                if (this.pendingHour > this.resolvedMax.getHours()) return true
            }
            return false
        },
        isSecondDisabled(s) {
            if (!this.pendingDate) return true
            if (this.resolvedMin && this.pendingDate === this.minDateStr) {
                if (this.pendingHour === this.resolvedMin.getHours() && this.pendingMinute === this.resolvedMin.getMinutes() && s < this.resolvedMin.getSeconds()) return true
                if (this.pendingHour === this.resolvedMin.getHours() && this.pendingMinute < this.resolvedMin.getMinutes()) return true
                if (this.pendingHour < this.resolvedMin.getHours()) return true
            }
            if (this.resolvedMax && this.pendingDate === this.maxDateStr) {
                if (this.pendingHour === this.resolvedMax.getHours() && this.pendingMinute === this.resolvedMax.getMinutes() && s >= this.resolvedMax.getSeconds()) return true
                if (this.pendingHour === this.resolvedMax.getHours() && this.pendingMinute > this.resolvedMax.getMinutes()) return true
                if (this.pendingHour > this.resolvedMax.getHours()) return true
            }
            return false
        },
        clampTime() {
            if (!this.pendingDate) return
            let changed = false
            if (this.resolvedMin && this.pendingDate === this.minDateStr) {
                if (this.pendingHour < this.resolvedMin.getHours()) {
                    this.pendingHour = this.resolvedMin.getHours()
                    this.pendingMinute = this.resolvedMin.getMinutes()
                    this.pendingSecond = this.resolvedMin.getSeconds()
                    changed = true
                } else if (this.pendingHour === this.resolvedMin.getHours() && this.pendingMinute < this.resolvedMin.getMinutes()) {
                    this.pendingMinute = this.resolvedMin.getMinutes()
                    this.pendingSecond = this.resolvedMin.getSeconds()
                    changed = true
                } else if (this.pendingHour === this.resolvedMin.getHours() && this.pendingMinute === this.resolvedMin.getMinutes() && this.pendingSecond < this.resolvedMin.getSeconds()) {
                    this.pendingSecond = this.resolvedMin.getSeconds()
                    changed = true
                }
            }
            if (this.resolvedMax && this.pendingDate === this.maxDateStr) {
                if (this.pendingHour > this.resolvedMax.getHours()) {
                    this.pendingHour = this.resolvedMax.getHours()
                    this.pendingMinute = Math.max(0, this.resolvedMax.getMinutes() - 1)
                    this.pendingSecond = 0
                    changed = true
                } else if (this.pendingHour === this.resolvedMax.getHours() && this.pendingMinute >= this.resolvedMax.getMinutes()) {
                    this.pendingMinute = Math.max(0, this.resolvedMax.getMinutes() - 1)
                    this.pendingSecond = 0
                    changed = true
                } else if (this.pendingHour === this.resolvedMax.getHours() && this.pendingMinute === this.resolvedMax.getMinutes() && this.pendingSecond >= this.resolvedMax.getSeconds()) {
                    this.pendingSecond = Math.max(0, this.resolvedMax.getSeconds() - 1)
                    changed = true
                }
            }
            if (!this._hourHasAvailableMinute(this.pendingHour, this.pendingDate)) {
                let found = false
                for (let h = this.pendingHour - 1; h >= 0; h--) {
                    if (this._hourHasAvailableMinute(h, this.pendingDate)) {
                        this.pendingHour = h
                        let lastMin = 59
                        if (this.resolvedMax && this.pendingDate === this.maxDateStr && h === this.resolvedMax.getHours()) {
                            lastMin = this.resolvedMax.getMinutes() - 1
                        }
                        this.pendingMinute = lastMin
                        this.pendingSecond = 0
                        changed = true
                        found = true
                        break
                    }
                }
                if (!found) {
                    for (let h = this.pendingHour + 1; h < 24; h++) {
                        if (this._hourHasAvailableMinute(h, this.pendingDate)) {
                            this.pendingHour = h
                            let firstMin = 0
                            if (this.resolvedMin && this.pendingDate === this.minDateStr && h === this.resolvedMin.getHours()) {
                                firstMin = this.resolvedMin.getMinutes()
                            }
                            this.pendingMinute = firstMin
                            this.pendingSecond = 0
                            changed = true
                            break
                        }
                    }
                }
            }
            if (changed) this.$nextTick(() => this.scrollTimeToCurrent())
        },
        isDayDisabled(year, month, day) {
            const date = new Date(year, month, day)
            if (this.resolvedMin && startOfDay(date) < startOfDay(this.resolvedMin)) return true
            if (this.resolvedMax && startOfDay(date) > startOfDay(this.resolvedMax)) return true
            if (this.activeDisabledDate && this.activeDisabledDate(date)) return true
            if (this.effectiveType === 'datetime') {
                const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`
                if ((this.resolvedMin && dateStr === this.minDateStr) || (this.resolvedMax && dateStr === this.maxDateStr)) {
                    let firstH = 0, lastH = 23
                    if (this.resolvedMin && dateStr === this.minDateStr) firstH = this.resolvedMin.getHours()
                    if (this.resolvedMax && dateStr === this.maxDateStr) lastH = this.resolvedMax.getHours()
                    let hasTime = false
                    for (let h = firstH; h <= lastH; h++) {
                        if (this._hourHasAvailableMinute(h, dateStr)) { hasTime = true; break }
                    }
                    if (!hasTime) return true
                }
            }
            return false
        },
        toggle() {
            if (this.disabled) return
            if (this.isOpen) this.closePicker()
            else this.openDropdown()
        },
        calcLeft(rect, panelWidth) {
            const viewW = window.innerWidth
            let left = rect.left + rect.width / 2 - panelWidth / 2
            if (left + panelWidth > viewW - 8) left = viewW - panelWidth - 8
            if (left < 8) left = 8
            return left
        },
        calcArrowLeft(rect, panelLeft, panelWidth) {
            const triggerCenter = rect.left + rect.width / 2
            let arrowLeft = triggerCenter - panelLeft
            if (arrowLeft < 20) arrowLeft = 20
            if (arrowLeft > panelWidth - 20) arrowLeft = panelWidth - 20
            return arrowLeft
        },
        openDropdown() {
            this.syncFromValue(this.modelValue)
            this.viewMode = 'calendar'
            this.effectiveType = this.type
            this.effectiveSeconds = this.seconds
            this.effectiveMin = null
            this.effectiveMax = null
            this.effectiveDisabledDate = null

            if (this.modal || this.headless) {
                this.isModal = true
                this.panelStyle = {}
                this.isOpen = true
                this.$emit('open')
                this.$nextTick(() => this.scrollTimeToCurrent())
                return
            }

            const trigger = this.$refs.trigger
            if (!trigger) {
                this.isModal = true
                this.panelStyle = {}
                this.isOpen = true
                this.$emit('open')
                this.$nextTick(() => this.scrollTimeToCurrent())
                return
            }

            this._triggerRect = trigger.getBoundingClientRect()
            const panelWidth = 300
            const gap = 4
            const left = this.calcLeft(this._triggerRect, panelWidth)
            const arrowLeft = this.calcArrowLeft(this._triggerRect, left, panelWidth)

            this.isModal = false
            this.dropAbove = false
            this.panelStyle = {
                position: 'fixed',
                top: this._triggerRect.bottom + gap + 'px',
                left: left + 'px',
                width: panelWidth + 'px',
                zIndex: 200015,
                '--dtp-arrow-left': arrowLeft + 'px',
            }
            this.isOpen = true
            this.$emit('open')

            this.$nextTick(() => {
                this.adjustPosition()
                this.scrollTimeToCurrent()
            })
        },
        adjustPosition() {
            if (this.isModal || !this.$refs.panel) return
            const panel = this.$refs.panel
            const panelRect = panel.getBoundingClientRect()
            const viewH = window.innerHeight
            const gap = 4
            const rect = this._triggerRect || this.$refs.trigger?.getBoundingClientRect()
            if (!rect) return

            const panelHeight = panelRect.height
            const panelWidth = 300
            const left = this.calcLeft(rect, panelWidth)
            const arrowLeft = this.calcArrowLeft(rect, left, panelWidth)
            const spaceBelow = viewH - rect.bottom - gap
            const spaceAbove = rect.top - gap

            if (spaceBelow >= panelHeight) return

            if (spaceAbove >= panelHeight) {
                this.dropAbove = true
                this.panelStyle = {
                    position: 'fixed',
                    bottom: (viewH - rect.top + gap) + 'px',
                    left: left + 'px',
                    width: panelWidth + 'px',
                    zIndex: 200015,
                    '--dtp-arrow-left': arrowLeft + 'px',
                }
                return
            }

            this.isModal = true
            this.dropAbove = false
            this.panelStyle = {}
        },
        closePicker() {
            this.isOpen = false
            this.headless = false
            this.$emit('close')
            if (this.pendingResolve) {
                this.pendingResolve(null)
                this.pendingResolve = null
            }
        },
        cancelPicker() {
            this.closePicker()
        },
        onOverlayClick() {
            this.closePicker()
        },
        clearValue() {
            this.$emit('update:modelValue', null)
            this.$emit('change', null)
        },
        navPrev() {
            if (this.viewMode === 'calendar') {
                if (this.viewMonth === 0) {
                    this.viewMonth = 11;
                    this.viewYear--
                } else this.viewMonth--
            } else if (this.viewMode === 'months') {
                this.viewYear--
            } else {
                this.viewYear -= 12
            }
        },
        navNext() {
            if (this.viewMode === 'calendar') {
                if (this.viewMonth === 11) {
                    this.viewMonth = 0;
                    this.viewYear++
                } else this.viewMonth++
            } else if (this.viewMode === 'months') {
                this.viewYear++
            } else {
                this.viewYear += 12
            }
        },
        cycleView() {
            if (this.viewMode === 'calendar') this.viewMode = 'months'
            else if (this.viewMode === 'months') this.viewMode = 'years'
            else this.viewMode = 'calendar'
        },
        selectMonth(m) {
            this.viewMonth = m
            this.viewMode = 'calendar'
        },
        selectYear(y) {
            this.viewYear = y
            this.viewMode = 'months'
        },
        selectDay(day) {
            if (day.disabled) return
            this.pendingDate = day.date

            const parts = day.date.split('-')
            const selectedYear = parseInt(parts[0], 10)
            const selectedMonth = parseInt(parts[1], 10) - 1
            if (selectedYear !== this.viewYear || selectedMonth !== this.viewMonth) {
                this.viewYear = selectedYear
                this.viewMonth = selectedMonth
            }

            this.clampTime()

            if (this.effectiveType === 'date') {
                const val = day.date
                if (!this.headless) {
                    this.$emit('update:modelValue', val)
                    this.$emit('change', val)
                }
                this.isOpen = false
                this.$emit('close')
                if (this.pendingResolve) {
                    this.pendingResolve(val)
                    this.pendingResolve = null
                }
                this.headless = false
            }
        },
        confirmPicker() {
            if (!this.pendingDate) return
            const parts = this.pendingDate.split('-')
            const val = formatValue(
                parseInt(parts[0], 10),
                parseInt(parts[1], 10) - 1,
                parseInt(parts[2], 10),
                this.pendingHour,
                this.pendingMinute,
                this.pendingSecond,
                this.effectiveType,
                this.effectiveSeconds
            )
            if (!this.headless) {
                this.$emit('update:modelValue', val)
                this.$emit('change', val)
            }
            this.isOpen = false
            this.$emit('close')
            if (this.pendingResolve) {
                this.pendingResolve(val)
                this.pendingResolve = null
            }
            this.headless = false
        },
        goToday() {
            const now = new Date()
            this.viewYear = now.getFullYear()
            this.viewMonth = now.getMonth()
            this.viewMode = 'calendar'

            const todayStr = this.todayDateStr
            if (!this.isDayDisabled(now.getFullYear(), now.getMonth(), now.getDate())) {
                this.pendingDate = todayStr
                this.pendingHour = now.getHours()
                this.pendingMinute = now.getMinutes()
                this.pendingSecond = now.getSeconds()

                if (this.effectiveType === 'date') {
                    if (!this.headless) {
                        this.$emit('update:modelValue', todayStr)
                        this.$emit('change', todayStr)
                    }
                    this.isOpen = false
                    this.$emit('close')
                    if (this.pendingResolve) {
                        this.pendingResolve(todayStr)
                        this.pendingResolve = null
                    }
                    this.headless = false
                } else {
                    this.$nextTick(() => this.scrollTimeToCurrent())
                }
            }
        },
        setHour(h) {
            if (this.isHourDisabled(h)) return
            this.pendingHour = h
            this.clampTime()
            this.scrollTimeToSelected()
        },
        setMinute(m) {
            if (this.isMinuteDisabled(m)) return
            this.pendingMinute = m
            this.clampTime()
            this.scrollTimeToSelected()
        },
        setSecond(s) {
            if (this.isSecondDisabled(s)) return
            this.pendingSecond = s
            this.scrollTimeToSelected()
        },
        scrollTimeToCurrent() {
            this.$nextTick(() => {
                this.scrollColTo(this.$refs.hourCol, this.pendingHour)
                this.scrollColTo(this.$refs.minuteCol, this.pendingMinute)
                if (this.effectiveSeconds) this.scrollColTo(this.$refs.secondCol, this.pendingSecond)
            })
        },
        scrollTimeToSelected() {
            this.$nextTick(() => {
                this.scrollColTo(this.$refs.hourCol, this.pendingHour)
                this.scrollColTo(this.$refs.minuteCol, this.pendingMinute)
                if (this.effectiveSeconds) this.scrollColTo(this.$refs.secondCol, this.pendingSecond)
            })
        },
        scrollColTo(col, index) {
            if (!col) return
            const item = col.children[index + 1]
            if (item) item.scrollIntoView({block: 'center', behavior: 'smooth'})
        },
        open() {
            if (!this.disabled) this.openDropdown()
        },
        close() {
            this.closePicker()
        },
        openPicker(opts = {}) {
            return new Promise((resolve) => {
                this.pendingResolve = resolve
                this.headless = true
                this.effectiveType = opts.type || this.type || 'datetime'
                this.effectiveSeconds = opts.seconds !== undefined ? opts.seconds : this.seconds
                this.effectiveMin = opts.min || null
                this.effectiveMax = opts.max || null
                this.effectiveDisabledDate = opts.disabledDate || null

                if (opts.value) {
                    this.syncFromValue(opts.value)
                } else {
                    const now = new Date()
                    this.viewYear = now.getFullYear()
                    this.viewMonth = now.getMonth()
                    this.pendingDate = null
                    this.pendingHour = now.getHours()
                    this.pendingMinute = now.getMinutes()
                    this.pendingSecond = 0
                }

                this.viewMode = 'calendar'
                this.isModal = true
                this.panelStyle = {}
                this.isOpen = true
                this.$emit('open')
                this.$nextTick(() => this.scrollTimeToCurrent())
            })
        },
        onEventBusOpen(opts) {
            this.openPicker(opts).then((val) => {
                if (val !== null) {
                    if (opts.onConfirm) opts.onConfirm(val)
                } else {
                    if (opts.onCancel) opts.onCancel()
                }
            })
        },
        onKeydown(e) {
            if (e.key === 'Escape' && this.isOpen) {
                e.stopPropagation()
                this.closePicker()
            }
        },
        onScrollResize() {
            if (this.isOpen && !this.isModal && !this.headless) {
                const trigger = this.$refs.trigger
                if (!trigger) return
                this._triggerRect = trigger.getBoundingClientRect()
                const panel = this.$refs.panel
                if (!panel) return
                const panelHeight = panel.getBoundingClientRect().height
                const viewH = window.innerHeight
                const gap = 4
                const rect = this._triggerRect
                const panelWidth = 300
                const left = this.calcLeft(rect, panelWidth)
                const arrowLeft = this.calcArrowLeft(rect, left, panelWidth)
                const spaceBelow = viewH - rect.bottom - gap
                const spaceAbove = rect.top - gap

                if (spaceBelow >= panelHeight) {
                    this.dropAbove = false
                    this.panelStyle = {
                        position: 'fixed',
                        top: rect.bottom + gap + 'px',
                        left: left + 'px',
                        width: panelWidth + 'px',
                        zIndex: 200015,
                        '--dtp-arrow-left': arrowLeft + 'px',
                    }
                } else if (spaceAbove >= panelHeight) {
                    this.dropAbove = true
                    this.panelStyle = {
                        position: 'fixed',
                        bottom: (viewH - rect.top + gap) + 'px',
                        left: left + 'px',
                        width: panelWidth + 'px',
                        zIndex: 200015,
                        '--dtp-arrow-left': arrowLeft + 'px',
                    }
                } else {
                    this.isModal = true
                    this.dropAbove = false
                    this.panelStyle = {}
                }
            }
        },
    },
    mounted() {
        document.addEventListener('keydown', this.onKeydown, true)
        window.addEventListener('scroll', this.onScrollResize, true)
        window.addEventListener('resize', this.onScrollResize)
        if (this.eventBus) {
            this.eventBus.$on('openDateTimePicker', this.onEventBusOpen)
        }
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onKeydown, true)
        window.removeEventListener('scroll', this.onScrollResize, true)
        window.removeEventListener('resize', this.onScrollResize)
        if (this.eventBus) {
            this.eventBus.$off('openDateTimePicker', this.onEventBusOpen)
        }
        if (this.pendingResolve) {
            this.pendingResolve(null)
            this.pendingResolve = null
        }
    },
}
</script>

<style>
.dtp {
    -webkit-user-select: none;
    user-select: none;
    --dtp-bg: #ffffff;
    --dtp-border: #e5e5ea;
    --dtp-text: #1c1c1e;
    --dtp-text2: #8e8e93;
    --dtp-accent: #007AFF;
    --dtp-accent-text: #ffffff;
    --dtp-hover: rgba(0, 0, 0, 0.04);
    --dtp-today: rgba(0, 122, 255, 0.1);
    --dtp-disabled: #c7c7cc;
    --dtp-panel-bg: #ffffff;
    --dtp-panel-border: #e5e5ea;
    --dtp-panel-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    --dtp-overlay-bg: rgba(0, 0, 0, 0.4);
    --dtp-input-bg: #ffffff;
    --dtp-input-border: #d1d1d6;
    --dtp-radius: 14px;
    --dtp-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.dtp-overlay {
    -webkit-user-select: none;
    user-select: none;
    --dtp-bg: #ffffff;
    --dtp-border: #e5e5ea;
    --dtp-text: #1c1c1e;
    --dtp-text2: #8e8e93;
    --dtp-accent: #007AFF;
    --dtp-accent-text: #ffffff;
    --dtp-hover: rgba(0, 0, 0, 0.04);
    --dtp-today: rgba(0, 122, 255, 0.1);
    --dtp-disabled: #c7c7cc;
    --dtp-panel-bg: #ffffff;
    --dtp-panel-border: #e5e5ea;
    --dtp-panel-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    --dtp-overlay-bg: rgba(0, 0, 0, 0.4);
    --dtp-radius: 14px;
    --dtp-font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.dtp-input-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    height: 36px;
    background: var(--dtp-input-bg);
    border: 1px solid var(--dtp-input-border);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: var(--dtp-font);
    font-size: 13px;
    color: var(--dtp-text);
    width: 100%;
    box-sizing: border-box;
}

.dtp-input-wrap:hover {
    border-color: var(--dtp-accent);
}

.dtp-input-wrap--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.dtp--error .dtp-input-wrap,
.dtp-input-wrap.dtp--error {
    border-color: #FF3B30;
}

.dtp-input-icon {
    flex-shrink: 0;
    color: var(--dtp-text2);
}

.dtp-input-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--dtp-text);
}

.dtp-input-placeholder {
    color: var(--dtp-text2);
}

.dtp-input-clear {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background: var(--dtp-hover);
    border-radius: 50%;
    cursor: pointer;
    color: var(--dtp-text2);
    padding: 0;
    transition: background 0.15s;
}

.dtp-input-clear:hover {
    background: rgba(0, 0, 0, 0.1);
}

.dtp-input-arrow {
    flex-shrink: 0;
    color: var(--dtp-text2);
    transition: transform 0.2s;
}

.dtp-overlay {
    position: fixed;
    inset: 0;
    z-index: 200025;
}

.dtp-overlay--modal {
    z-index: 200030;
    background: var(--dtp-overlay-bg);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.dtp-panel {
    background: var(--dtp-panel-bg);
    border-radius: var(--dtp-radius);
    box-shadow: var(--dtp-panel-shadow);
    overflow: visible;
    font-family: var(--dtp-font);
    width: 300px;
    animation: dtpPanelIn 0.18s ease-out;
    border: 1px solid var(--dtp-panel-border);
    position: relative;
}

.dtp-panel--modal {
    width: 320px;
    max-width: 95vw;
    animation: dtpModalIn 0.2s ease-out;
}

.dtp-panel:not(.dtp-panel--modal)::before,
.dtp-panel:not(.dtp-panel--modal)::after {
    content: '';
    position: absolute;
    left: var(--dtp-arrow-left, 50%);
    transform: translateX(-50%);
    pointer-events: none;
}

.dtp-panel:not(.dtp-panel--modal):not(.dtp-panel--above)::before {
    top: -7px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 7px solid var(--dtp-panel-border);
}

.dtp-panel:not(.dtp-panel--modal):not(.dtp-panel--above)::after {
    top: -6px;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 6px solid var(--dtp-panel-bg);
}

.dtp-panel--above:not(.dtp-panel--modal)::before {
    bottom: -7px;
    top: auto;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 7px solid var(--dtp-panel-border);
}

.dtp-panel--above:not(.dtp-panel--modal)::after {
    bottom: -6px;
    top: auto;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 6px solid var(--dtp-panel-bg);
}

@keyframes dtpPanelIn {
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.dtp-panel--above:not(.dtp-panel--modal) {
    animation: dtpPanelInAbove 0.18s ease-out;
}

@keyframes dtpPanelInAbove {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes dtpModalIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.dtp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px 8px;
}

.dtp-nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    color: var(--dtp-text);
    transition: background 0.15s;
    padding: 0;
}

.dtp-nav-btn:hover {
    background: var(--dtp-hover);
}

.dtp-header-label {
    flex: 1;
    text-align: center;
    border: none;
    background: none;
    font-size: 15px;
    font-weight: 600;
    color: var(--dtp-text);
    cursor: pointer;
    border-radius: 8px;
    padding: 6px 12px;
    transition: background 0.15s;
    font-family: var(--dtp-font);
}

.dtp-header-label:hover {
    background: var(--dtp-hover);
}

.dtp-calendar {
    padding: 0 12px;
}

.dtp-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    margin-bottom: 4px;
}

.dtp-weekdays span {
    font-size: 11px;
    font-weight: 600;
    color: var(--dtp-text2);
    padding: 4px 0;
    text-transform: uppercase;
}

.dtp-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    justify-items: center;
}

.dtp-day {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 400;
    color: var(--dtp-text);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-family: var(--dtp-font);
    padding: 0;
}

.dtp-day:hover:not(.dtp-day--disabled):not(.dtp-day--selected) {
    background: var(--dtp-hover);
}

.dtp-day--other {
    color: var(--dtp-disabled);
}

.dtp-day--today:not(.dtp-day--selected) {
    background: var(--dtp-today);
    color: var(--dtp-accent);
    font-weight: 600;
}

.dtp-day--selected {
    background: var(--dtp-accent);
    color: var(--dtp-accent-text);
    font-weight: 600;
}

.dtp-day--disabled {
    color: var(--dtp-disabled);
    cursor: not-allowed;
    opacity: 0.4;
}

.dtp-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 8px 12px 12px;
}

.dtp-grid-item {
    padding: 10px 4px;
    border: none;
    background: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    color: var(--dtp-text);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-family: var(--dtp-font);
    text-align: center;
}

.dtp-grid-item:hover:not(.dtp-grid-item--selected) {
    background: var(--dtp-hover);
}

.dtp-grid-item--current:not(.dtp-grid-item--selected) {
    color: var(--dtp-accent);
    font-weight: 600;
}

.dtp-grid-item--selected {
    background: var(--dtp-accent);
    color: var(--dtp-accent-text);
    font-weight: 600;
}

.dtp-time {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 12px;
    border-top: 1px solid var(--dtp-border);
}

.dtp-time-col {
    width: 56px;
    height: 120px;
    overflow-y: auto;
    scroll-snap-type: y mandatory;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
    background: var(--dtp-hover);
}

.dtp-time-col::-webkit-scrollbar {
    width: 0;
    display: none;
}

.dtp-time-spacer {
    height: 42px;
    scroll-snap-align: none;
}

.dtp-time-sep {
    font-size: 18px;
    font-weight: 700;
    color: var(--dtp-text);
    padding: 0 2px;
    align-self: center;
}

.dtp-time-item {
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    font-size: 15px;
    font-weight: 500;
    color: var(--dtp-text);
    cursor: pointer;
    scroll-snap-align: center;
    transition: background 0.15s, color 0.15s;
    font-family: var(--dtp-font);
    padding: 0;
    border-radius: 6px;
}

.dtp-time-item:hover:not(.dtp-time-item--selected) {
    background: rgba(0, 0, 0, 0.06);
}

.dtp-time-item--selected {
    background: var(--dtp-accent);
    color: var(--dtp-accent-text);
    font-weight: 600;
}

.dtp-time-item--disabled {
    color: var(--dtp-disabled);
    cursor: not-allowed;
    opacity: 0.35;
}

.dtp-time-item--disabled:hover {
    background: none;
}

.dtp-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px 12px;
    border-top: 1px solid var(--dtp-border);
}

.dtp-footer-right {
    display: flex;
    gap: 8px;
}

.dtp-footer-btn {
    border: none;
    background: none;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 8px;
    transition: background 0.15s, opacity 0.15s;
    font-family: var(--dtp-font);
}

.dtp-footer-btn--today {
    color: var(--dtp-accent);
}

.dtp-footer-btn--today:hover {
    background: var(--dtp-today);
}

.dtp-footer-btn--cancel {
    color: var(--dtp-text2);
}

.dtp-footer-btn--cancel:hover {
    background: var(--dtp-hover);
}

.dtp-footer-btn--confirm {
    background: var(--dtp-accent);
    color: var(--dtp-accent-text);
    font-weight: 600;
}

.dtp-footer-btn--confirm:hover {
    opacity: 0.9;
}

.dtp-footer-btn--confirm:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.dtp-fade-enter-active {
    transition: opacity 0.18s ease-out;
}

.dtp-fade-leave-active {
    transition: opacity 0.12s ease-in;
}

.dtp-fade-enter-from,
.dtp-fade-leave-to {
    opacity: 0;
}

html.dark .dtp,
html.dark .dtp-overlay {
    --dtp-bg: #15171C;
    --dtp-border: #26272F;
    --dtp-text: #F5F5F5;
    --dtp-text2: #8e8e93;
    --dtp-accent: #0b8e62;
    --dtp-accent-text: #ffffff;
    --dtp-hover: rgba(255, 255, 255, 0.06);
    --dtp-today: rgba(11, 142, 98, 0.15);
    --dtp-disabled: #48484a;
    --dtp-panel-bg: #1c1c1e;
    --dtp-panel-border: #38383a;
    --dtp-panel-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    --dtp-overlay-bg: rgba(0, 0, 0, 0.5);
    --dtp-input-bg: #15171C;
    --dtp-input-border: #26272F;
}

html.dark .dtp-input-clear:hover {
    background: rgba(255, 255, 255, 0.12);
}

html.dark .dtp-time-item:hover:not(.dtp-time-item--selected) {
    background: rgba(255, 255, 255, 0.08);
}

html.midnight .dtp,
html.midnight .dtp-overlay {
    --dtp-bg: #1A1714;
    --dtp-border: #2C2823;
    --dtp-text: #EAE6E1;
    --dtp-text2: #8e8e93;
    --dtp-accent: #D4915C;
    --dtp-accent-text: #ffffff;
    --dtp-hover: rgba(255, 255, 255, 0.06);
    --dtp-today: rgba(212, 145, 92, 0.15);
    --dtp-disabled: #504A43;
    --dtp-panel-bg: #211D19;
    --dtp-panel-border: #3D3832;
    --dtp-panel-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    --dtp-overlay-bg: rgba(0, 0, 0, 0.5);
    --dtp-input-bg: #1A1714;
    --dtp-input-border: #2C2823;
}

html.midnight .dtp-input-clear:hover {
    background: rgba(255, 255, 255, 0.12);
}

html.midnight .dtp-time-item:hover:not(.dtp-time-item--selected) {
    background: rgba(255, 255, 255, 0.08);
}

html.cream .dtp,
html.cream .dtp-overlay {
    --dtp-bg: #FEFCF8;
    --dtp-border: #E8E3DA;
    --dtp-text: #3D3832;
    --dtp-text2: #A39B8F;
    --dtp-accent: #059669;
    --dtp-accent-text: #ffffff;
    --dtp-hover: rgba(5, 150, 105, 0.06);
    --dtp-today: rgba(5, 150, 105, 0.12);
    --dtp-disabled: #C4BCB0;
    --dtp-panel-bg: #F5F1EA;
    --dtp-panel-border: #DDD7CC;
    --dtp-panel-shadow: 0 12px 40px rgba(76, 70, 60, 0.15);
    --dtp-overlay-bg: rgba(0, 0, 0, 0.18);
    --dtp-input-bg: #FEFCF8;
    --dtp-input-border: #DDD7CC;
}

</style>
